import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { User, Star, Calendar, Award } from 'lucide-react';
import ReferralsPanel from '../components/ReferralsPanel';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateUser({ ...user, ...formData });
    setEditing(false);
    toast({
      title: 'Perfil actualizado',
      description: 'Tus cambios han sido guardados'
    });
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Mi Perfil</h1>

        {/* Profile Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-6">
              <Avatar className="w-24 h-24">
                <AvatarFallback className="text-3xl">{user.name[0]}</AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-2xl">{user.name}</CardTitle>
                <p className="text-gray-600">{user.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge>{user.role === 'mobilitier' ? 'Mobilitier' : 'Usuario'}</Badge>
                  {user.verified && (
                    <Badge variant="default" className="bg-green-600">Verificado</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-6 text-center">
              <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500 fill-yellow-500" />
              <p className="text-2xl font-bold">{user.rating || 0}</p>
              <p className="text-sm text-gray-600">Calificación</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <Award className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{user.trips || 0}</p>
              <p className="text-sm text-gray-600">Viajes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <Calendar className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold">
                {user.joined_date ? new Date(user.joined_date).getFullYear() : new Date().getFullYear()}
              </p>
              <p className="text-sm text-gray-600">Miembro desde</p>
            </CardContent>
          </Card>
        </div>

        {/* Edit Profile */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Información Personal</CardTitle>
              {!editing && (
                <Button variant="outline" onClick={() => setEditing(true)}>
                  Editar
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {editing ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nombre Completo</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">Guardar</Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setEditing(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Nombre</Label>
                  <p className="text-lg">{user.name}</p>
                </div>
                <div>
                  <Label>Email</Label>
                  <p className="text-lg">{user.email}</p>
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <p className="text-lg">{user.phone}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {user.role === 'mobilitier' && user.earnings && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Ganancias</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <p className="text-4xl font-bold text-green-600">${user.earnings.toLocaleString()} MXN</p>
                <p className="text-gray-600 mt-2">Ganancias totales acumuladas</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-6">
          <ReferralsPanel />
        </div>
      </div>
    </div>
  );
};

export default Profile;
